import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import GraphDiagram from "@/components/diagrams/GraphDiagram";
import CorrelationDiagram from "@/components/diagrams/CorrelationDiagram";
import DriftDiagram from "@/components/diagrams/DriftDiagram";
import ArchitectureDiagram from "@/components/diagrams/ArchitectureDiagram";
import ProvenanceDiagram from "@/components/diagrams/ProvenanceDiagram";

const SECTIONS = [
  {
    id: "context-graph",
    n: "01",
    name: "Context Graph",
    tag: "Organizational context, structured",
    intro: "A living, queryable model of the entities, systems, workflows, decisions, and relationships that define how your organization operates.",
    how: [
      "Ingests structure and metadata from the systems where work already happens — ERP, CRM, data platforms, collaboration and documentation tools.",
      "Resolves entities across sources: people, teams, systems, processes, assets, and decisions are unified into one coherent graph.",
      "Captures decision history alongside the context that shaped it — who owned it, what informed it, what it changed.",
      "Exposes context as a reusable layer for AI systems, copilots, and human operators through APIs and query interfaces.",
    ],
    artifacts: ["Entity & relationship model", "Decision lineage records", "Context API for AI systems", "Ownership maps"],
    diagram: <GraphDiagram />,
  },
  {
    id: "correlation-engine",
    n: "02",
    name: "Correlation Engine",
    tag: "Signals, connected",
    intro: "An analytical layer that finds meaningful relationships across events, workflows, decisions, and outcomes — the patterns isolated dashboards never show.",
    how: [
      "Consumes operational signals across the Context Graph: deployments, incidents, spend, adoption, org changes, workflow events.",
      "Identifies statistically and semantically meaningful relationships — leading indicators, co-movements, and causal candidates.",
      "Ranks patterns by business relevance so teams see the few connections that matter, not a wall of correlations.",
      "Feeds validated patterns back into the graph, where they become permanent institutional knowledge.",
    ],
    artifacts: ["Pattern ranking & relevance scoring", "Cross-workflow signal links", "Leading-indicator surfacing", "Insight feed"],
    diagram: <CorrelationDiagram />,
  },
  {
    id: "change-observability",
    n: "03",
    name: "Change Observability",
    tag: "Drift, made visible",
    intro: "Continuous monitoring of how systems, workflows, knowledge, and ownership evolve — so drift is detected while it is still cheap to correct.",
    how: [
      "Baselines expected behavior for processes, systems, and knowledge domains derived from the Context Graph.",
      "Detects drift across three dimensions: process drift, knowledge and decision drift, and system change.",
      "Traces blast radius through the graph — which teams, decisions, and AI systems a change actually touches.",
      "Alerts with context: not just that something changed, but why it matters and who should care.",
    ],
    artifacts: ["Drift timelines & baselines", "Blast-radius tracing", "Change impact alerts", "Knowledge freshness scoring"],
    diagram: <DriftDiagram />,
  },
];

export default function Product() {
  useEffect(() => {
    document.title = "Product — DotIQ";
  }, []);

  return (
    <main className="pt-[72px]" data-testid="product-page">
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-36">
          <p className="label-mono" data-testid="product-eyebrow">Product — technical view</p>
          <MaskedLines
            className="mt-8 font-display text-4xl font-medium leading-[1.05] tracking-tighter sm:text-6xl lg:text-7xl"
            lines={["The machinery behind", "institutional intelligence."]}
          />
          <Reveal delay={0.5}>
            <p className="mt-10 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
              Three mechanisms, one compounding system. The Context Graph
              supplies structure, the Correlation Engine supplies relationships,
              and Change Observability keeps both honest over time.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 sm:py-24" data-testid="architecture-section">
          <Reveal>
            <p className="label-mono">Architecture</p>
            <h2 className="mt-6 max-w-3xl font-display text-3xl font-medium tracking-tight sm:text-4xl">
              Sits between your systems and your AI.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <div className="panel overflow-x-auto p-6 sm:p-10">
              <ArchitectureDiagram />
            </div>
          </Reveal>
        </div>
      </section>

      {SECTIONS.map((s, i) => (
        <section key={s.id} className="border-b border-white/10" data-testid={`product-section-${s.id}`}>
          <div className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 sm:py-28">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
              <Reveal className="lg:col-span-4">
                <span className="font-mono text-xs text-signal">{s.n}</span>
                <h2 className="mt-4 font-display text-3xl font-medium tracking-tight sm:text-4xl">{s.name}</h2>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">{s.tag}</p>
                <p className="mt-6 text-sm leading-relaxed text-white/55 sm:text-base">{s.intro}</p>
                <div className="mt-8 panel p-6">{s.diagram}</div>
              </Reveal>
              <div className="lg:col-span-8">
                <Reveal delay={0.1}>
                  <p className="label-mono">How it works</p>
                  <ol className="mt-6 flex flex-col gap-px bg-white/10">
                    {s.how.map((step, j) => (
                      <li key={j} className="flex gap-6 bg-ink p-6 sm:p-7" data-testid={`product-${s.id}-step-${j}`}>
                        <span className="font-mono text-xs text-white/30">{String(j + 1).padStart(2, "0")}</span>
                        <p className="text-sm leading-relaxed text-white/70">{step}</p>
                      </li>
                    ))}
                  </ol>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="label-mono mt-12">What you get</p>
                  <div className="mt-6 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2">
                    {s.artifacts.map((a) => (
                      <div key={a} className="bg-ink px-6 py-5" data-testid={`product-${s.id}-artifact-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <span className="font-mono text-xs text-white/60">{a}</span>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 sm:py-28" data-testid="provenance-section">
          <Reveal>
            <p className="label-mono">Provenance</p>
            <h2 className="mt-6 max-w-3xl font-display text-3xl font-medium tracking-tight sm:text-4xl">
              Trustworthy AI, made tangible.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/55">
              Every DotIQ recommendation carries its full evidence chain — from
              the originating source system, through the relationship and
              reasoning, to the recommended action. Leaders inspect the why,
              not just the what.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <div className="panel overflow-x-auto p-6 sm:p-10">
              <ProvenanceDiagram />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32" data-testid="product-cta-section">
        <Reveal>
          <p className="label-mono">Deployment</p>
          <h2 className="mt-6 max-w-3xl font-display text-3xl font-medium tracking-tight sm:text-5xl">
            Runs inside your perimeter. Answers to your governance.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/55">
            DotIQ deploys within your security boundary, respects your
            data-residency requirements, and never trains shared models on your
            data. SSO, role-based access, and full audit trails are table
            stakes — not roadmap items.
          </p>
          <div className="mt-12 grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5" data-testid="trust-architecture-strip">
            {[
              { t: "Permissions", d: "Inherits access controls from source systems — users only see what they're already allowed to see." },
              { t: "Provenance", d: "Every insight traces back to the systems, records, and decisions it came from." },
              { t: "Confidence", d: "Recommendations carry explicit confidence scores — never black-box answers." },
              { t: "Temporal context", d: "Knows when something was true, not just that it was — stale knowledge is flagged, not served." },
              { t: "Explainability", d: "Evidence, timeline, and reasoning behind every conclusion, inspectable on demand." },
            ].map((item, i) => (
              <div key={item.t} className="bg-ink p-6" data-testid={`trust-item-${item.t.toLowerCase().replace(/\s/g, "-")}`}>
                <span className="font-mono text-xs text-signal">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-base font-medium tracking-tight">{item.t}</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/50">{item.d}</p>
              </div>
            ))}
          </div>
          <Link
            to="/apply"
            data-testid="product-apply-cta"
            className="group mt-10 inline-flex items-center gap-3 bg-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white"
          >
            Apply as Design Partner
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
